import React, { useState, useEffect } from 'react';
import {
    Button,
    Input,
    Tag,
    Typography,
    Card,
    Space,
    message,
    Popconfirm,
    Spin,
    Empty,
    Modal,
} from 'antd';
import {
    PlusOutlined,
    GlobalOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { getCompanyDomains, updateCompanyDomains, deleteCompanyDomains } from '../../services/adminServices';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const ManageDomainsTab = () => {
    const { t } = useTranslation();
    const [domains, setDomains] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        fetchDomains();
    }, []);

    const fetchDomains = async () => {
        setLoading(true);
        try {
            const res = await getCompanyDomains();
            if (res.status === 200) {
                setDomains(res.data?.companyDomains || []);
            } else {
                message.error(res.message || t('Failed to fetch domains'));
            }
        } catch (error) {
            message.error(t('An error occurred while fetching domains'));
        } finally {
            setLoading(false);
        }
    };

    const handleAddDomain = async () => {
        const domain = inputValue.trim().toLowerCase();
        if (!domain) {
            message.warning(t('Please enter a domain name'));
            return;
        }

        const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
        if (!domainRegex.test(domain)) {
            message.error(t('Please enter a valid domain name (e.g., gmail.com)'));
            return;
        }

        if (domains.includes(domain)) {
            message.warning(t('This domain is already added'));
            return;
        }

        setActionLoading(true);
        try {
            const res = await updateCompanyDomains([domain]);
            if (res.status === 200) {
                message.success(t('Domain added successfully'));
                setDomains(prev => [...prev, domain]);
                setInputValue('');
            } else {
                message.error(res.message || t('Failed to add domain'));
            }
        } catch (error) {
            message.error(t('An error occurred while adding domain'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteDomain = async (domainToDelete) => {
        setActionLoading(true);
        try {
            const res = await deleteCompanyDomains([domainToDelete]);
            if (res.status === 200) {
                message.success(t('Domain deleted successfully'));
                setDomains(prev => prev.filter(d => d !== domainToDelete));
            } else {
                message.error(res.message || t('Failed to delete domain'));
            }
        } catch (error) {
            message.error(t('An error occurred while deleting domain'));
        } finally {
            setActionLoading(false);
        }
    };

    const showDeleteConfirm = (domainToDelete) => {
        Modal.confirm({
            title: t('Delete Domain'),
            icon: <ExclamationCircleOutlined className="text-red-500" />,
            content: t('Are you really want to delete domain? After that no one will be able to login using that domain.'),
            okText: t('Delete'),
            okType: 'danger',
            cancelText: t('Cancel'),
            centered: true,
            className: 'delete-domain-modal',
            onOk() {
                return handleDeleteDomain(domainToDelete);
            },
        });
    };

    return (
        <Card className="p-0 border-0 shadow-none">
            <div className="flex flex-col gap-8 w-full">
                <div className="mb-2">
                    <Title level={5} className="!mb-2 flex items-center gap-2 text-gray-800">
                        <GlobalOutlined className="text-blue-500 text-lg" />
                        {t('Manage Email Domains')}
                    </Title>
                    <Text className="text-gray-500 text-sm block ml-7">
                        {t('Add your company domains to manage user access and identification.')}
                    </Text>
                </div>

                <div className="max-w-[500px]">
                    <div className="flex gap-3 w-full">
                        <Input
                            placeholder={t('eg. gmail.com')}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onPressEnter={handleAddDomain}
                            disabled={actionLoading}
                            size="medium"
                            className="rounded-lg shadow-sm border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all lowercase placeholder:normal-case font-medium h-[44px]"
                        />
                        <Button
                            type="primary"
                            onClick={handleAddDomain}
                            loading={actionLoading}
                            icon={<PlusOutlined />}
                            size="medium"
                            className="rounded-lg bg-blue-600 hover:bg-blue-700 border-none h-[44px] px-6 font-semibold shadow-md hover:shadow-lg transition-all"
                        >
                            {t('Add Domain')}
                        </Button>
                    </div>
                </div>

                <div className="bg-gray-50/50 rounded-xl border border-gray-100 p-6 transition-all duration-300">
                    <div className="mb-4 flex items-center gap-2">
                        <InfoCircleOutlined className="text-gray-400" />
                        <span className="text-gray-600 font-medium text-sm">{t('Allowed Domains list')}</span>
                    </div>

                    {loading ? (
                        <div className="text-center py-10">
                            <Spin tip={t("Fetching domains...")} />
                        </div>
                    ) : domains.length > 0 ? (
                        <div className="flex flex-wrap gap-4 py-2">
                            {domains.map(domain => (
                                <Tag
                                    key={domain}
                                    closable
                                    onClose={(e) => {
                                        e.preventDefault();
                                        showDeleteConfirm(domain);
                                    }}
                                    className="px-4 py-2 rounded-full text-blue-700 bg-white border border-blue-100 flex items-center gap-2 shadow-sm hover:shadow-md hover:border-blue-200 transition-all cursor-default group"
                                    closeIcon={
                                        <span className="ml-1 opacity-90 group-hover:opacity-100 transition-opacity">
                                            <DeleteOutlined className="text-red-400 hover:text-red-500 text-[13px]" />
                                        </span>
                                    }
                                >
                                    <span className="font-medium text-lg">{domain}</span>
                                </Tag>
                            ))}
                        </div>
                    ) : (
                        <Empty
                            description={<span className="text-gray-400 italic">{t('No domains registered yet')}</span>}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            className="py-10"
                        />
                    )}
                </div>
            </div>
        </Card>
    );
};

export default ManageDomainsTab;
